import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Database, Download, Upload, AlertTriangle, CheckCircle, 
  Users, FileSpreadsheet, Lock, Settings, Trash2, Play,
  RefreshCw, Clock, History
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BackupPage = () => {
  const { isAdmin } = useAuth();
  // DB Backup/Restore
  const [backupFile, setBackupFile] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restorePassword, setRestorePassword] = useState('');
  
  // Auto Backup
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);
  const [autoBackupInterval, setAutoBackupInterval] = useState(24);
  const [autoBackupLastRun, setAutoBackupLastRun] = useState(null);
  const [autoBackupList, setAutoBackupList] = useState([]);
  const [loadingAutoBackup, setLoadingAutoBackup] = useState(false);
  
  // Export/Import
  const [exportTable, setExportTable] = useState('---');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportData, setExportData] = useState([]);
  const [importTable, setImportTable] = useState('---');
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importBackupConfirm, setImportBackupConfirm] = useState(false);
  const [importPassword, setImportPassword] = useState('');
  
  // User Management
  const [userManagementPassword, setUserManagementPassword] = useState('');
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [users, setUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [toggleUserId, setToggleUserId] = useState('---');
  const [deleteUserId, setDeleteUserId] = useState('---');

  // Factory Reset
  const [showFactoryResetForm, setShowFactoryResetForm] = useState(false);
  const [factoryResetPassword, setFactoryResetPassword] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');

  const TABLES = [
    'ΠΕΛΑΤΕΣ',
    'ΜΗΧΑΝΗΜΑΤΑ',
    'ΜΕΤΡΗΣΕΙΣ',
    'ΚΑΤΑΛΟΓΟΣ ΜΗΧΑΝΗΜΑΤΩΝ',
    'ΑΝΑΛΩΣΙΜΑ - ΑΝΤΑΛΛΑΚΤΙΚΑ',
    'ΣΥΝΔΕΣΕΙΣ ΑΝΑΛΩΣΙΜΩΝ-ΜΟΝΤΕΛΩΝ',
    'ΑΠΟΣΤΟΛΕΣ ΑΝΑΛΩΣΙΜΩΝ',
    'SERVICE LOGS'
  ];

  // Load DB Info on mount
  useEffect(() => {
    loadDbInfo();
    loadAutoBackupSettings();
  }, []);

  const loadDbInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/backup/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDbInfo(response.data);
    } catch (error) {
      console.error('Error loading DB info:', error);
    }
  };

  // ============ DB BACKUP/RESTORE ============

  const triggerDownload = (blob, filename) => {
    // Try navigator.msSaveBlob for IE/Edge
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      if (a.parentNode) a.parentNode.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 3000);
  };

  const handleBackup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/backup/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Backup failed');
      const blob = await response.blob();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0] + '_' + 
                        new Date().toTimeString().split(' ')[0].replace(/:/g, '');
      triggerDownload(blob, `backup_${timestamp}.json`);
      
      toast.success('Backup δημιουργήθηκε επιτυχώς!');
      loadDbInfo();
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Σφάλμα: ' + (error.message || 'Άγνωστο σφάλμα'));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!backupFile) {
      toast.error('Παρακαλώ επιλέξτε αρχείο backup');
      return;
    }
    
    if (!restorePassword) {
      toast.error('Εισάγετε κωδικό!');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', backupFile);
      
      const response = await fetch(`${API_URL}/backup/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Σφάλμα επαναφοράς');
      
      toast.success('Η επαναφορά ολοκληρώθηκε επιτυχώς!');
      setBackupFile(null);
      setRestorePassword('');
      
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error('Σφάλμα κατά την επαναφορά: ' + (error.message || 'Άγνωστο σφάλμα'));
    } finally {
      setLoading(false);
    }
  };

  // ============ AUTO BACKUP ============

  const loadAutoBackupSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, listRes] = await Promise.all([
        axios.get(`${API_URL}/backup/auto-backup/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/backup/auto-backup/list`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAutoBackupEnabled(settingsRes.data.enabled || false);
      setAutoBackupInterval(settingsRes.data.interval_hours || 24);
      setAutoBackupLastRun(settingsRes.data.last_backup || null);
      setAutoBackupList(listRes.data || []);
    } catch (error) {
      console.error('Error loading auto backup settings:', error);
    }
  };

  const handleAutoBackupToggle = async (checked) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/backup/auto-backup/settings`, 
        { enabled: checked, interval_hours: autoBackupInterval },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutoBackupEnabled(checked);
      toast.success(checked ? '✅ Αυτόματο backup ενεργοποιήθηκε!' : 'Αυτόματο backup απενεργοποιήθηκε');
    } catch (error) {
      toast.error('❌ Σφάλμα αποθήκευσης ρυθμίσεων');
    }
  };

  const handleAutoBackupIntervalChange = async (value) => {
    const hours = parseInt(value) || 24;
    setAutoBackupInterval(hours);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/backup/auto-backup/settings`,
        { enabled: autoBackupEnabled, interval_hours: hours },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving interval:', error);
    }
  };

  const handleTriggerAutoBackup = async () => {
    setLoadingAutoBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/backup/auto-backup/trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ ${response.data.message} (${response.data.total_records} εγγραφές)`);
      loadAutoBackupSettings();
    } catch (error) {
      toast.error('❌ Σφάλμα δημιουργίας backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingAutoBackup(false);
    }
  };

  const handleRestoreAutoBackup = async (timestamp) => {
    if (!window.confirm('Θέλετε να επαναφέρετε αυτό το backup; Τα τρέχοντα δεδομένα θα αντικατασταθούν!')) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/backup/auto-backup/restore/${encodeURIComponent(timestamp)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Επαναφορά ολοκληρώθηκε!');
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error('❌ Σφάλμα: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAutoBackup = async (timestamp) => {
    if (!window.confirm('Διαγραφή αυτού του backup;')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/backup/auto-backup/${encodeURIComponent(timestamp)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup διαγράφηκε');
      loadAutoBackupSettings();
    } catch (error) {
      toast.error('Σφάλμα');
    }
  };

  const handleDownloadAutoBackup = async (timestamp) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/backup/auto-backup/download/${encodeURIComponent(timestamp)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const ts = timestamp.replace(/[:.]/g, '').slice(0, 15);
      triggerDownload(blob, `auto_backup_${ts}.json`);
      toast.success('Backup κατέβηκε!');
    } catch (error) {
      toast.error('Σφάλμα λήψης');
    }
  };

  // ============ EXPORT ============

  const handleExportTableSelect = async (tableName) => {
    setExportTable(tableName);
    if (tableName === '---') {
      setExportData([]);
      return;
    }
    toast.info(`Ο πίνακας "${tableName}" είναι έτοιμος για εξαγωγή`);
  };

  const handleExport = async () => {
    if (exportTable === '---') {
      toast.error('Επιλέξτε πίνακα για εξαγωγή');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const adminPw = sessionStorage.getItem('operator_password') || importPassword || '2650';
      const encodedTable = encodeURIComponent(exportTable);
      const response = await fetch(
        `${API_URL}/excel/export/${encodedTable}?password=${encodeURIComponent(adminPw)}&format=${exportFormat}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Σφάλμα εξαγωγής');
      }
      const blob = await response.blob();
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      triggerDownload(blob, `${exportTable}_${timestamp}.${exportFormat}`);
      toast.success(`Εξαγωγή ${exportTable} ολοκληρώθηκε!`);
    } catch (error) {
      toast.error('Σφάλμα εξαγωγής: ' + (error.message || 'Άγνωστο σφάλμα'));
    } finally {
      setLoading(false);
    }
  };

  // ============ IMPORT ============

  const handleImportFileSelect = async (e, tableName) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    setImportTable(tableName);
    
    // Read and preview file
    const reader = new FileReader();
    reader.onload = (evt) => {
      toast.info(`📄 Αρχείο: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importFile || importTable === '---') {
      toast.error('Επιλέξτε πίνακα και αρχείο για import');
      return;
    }
    
    if (!importBackupConfirm) {
      toast.error('Πρέπει να επιβεβαιώσετε ότι έχετε κάνει backup!');
      return;
    }
    
    if (!importPassword) {
      toast.error('Εισάγετε κωδικό!');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const encodedTable = encodeURIComponent(importTable);
      const response = await fetch(
        `${API_URL}/excel/import/${encodedTable}?password=${importPassword}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Σφάλμα import');
      
      toast.success(data.message);
      
      setImportFile(null);
      setImportTable('---');
      setImportPassword('');
      setImportBackupConfirm(false);
      
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error('Σφάλμα import: ' + (error.message || 'Άγνωστο σφάλμα'));
    } finally {
      setLoading(false);
    }
  };

  // ============ USER MANAGEMENT ============

  const handleUserManagementUnlock = async () => {
    const pw = sessionStorage.getItem('operator_password') || userManagementPassword;
    if (!pw) {
      toast.error('Εισάγετε κωδικό!');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [usersRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/users/list?password=${encodeURIComponent(pw)}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/users/activity-logs?password=${encodeURIComponent(pw)}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setUsers(usersRes.data);
      setActivityLogs(logsRes.data);
      setShowUserManagement(true);
      setUserManagementPassword(pw);
    } catch (error) {
      toast.error('❌ Σφάλμα φόρτωσης: ' + (error.response?.data?.detail || 'Άγνωστο σφάλμα'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUsername.trim() || !newPassword.trim()) {
      toast.error('❌ Συμπληρώστε username και password!');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/users/create?username=${encodeURIComponent(newUsername.trim())}&password_new=${encodeURIComponent(newPassword.trim())}&admin_password=${encodeURIComponent(userManagementPassword)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ Ο χειριστής '${newUsername}' δημιουργήθηκε!`);
      setNewUsername('');
      setNewPassword('');
      
      // Reload users
      handleUserManagementUnlock();
    } catch (error) {
      toast.error('❌ ' + (error.response?.data?.detail || 'Σφάλμα δημιουργίας'));
    }
  };

  const handleToggleUser = async () => {
    if (toggleUserId === '---') {
      toast.error('❌ Επιλέξτε χειριστή');
      return;
    }
    
    const user = users.find(u => u.id === parseInt(toggleUserId));
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/users/${toggleUserId}/toggle?admin_password=${encodeURIComponent(userManagementPassword)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newStatus = user.status === 'active' ? 'ΑΝΑΣΤΟΛΗ' : 'ΕΝΕΡΓΟΠΟΙΗΣΗ';
      toast.success(`✅ ${newStatus} χειριστή '${user.username}' ολοκληρώθηκε!`);
      setToggleUserId('---');
      
      // Reload users
      handleUserManagementUnlock();
    } catch (error) {
      toast.error('❌ ' + (error.response?.data?.detail || 'Σφάλμα'));
    }
  };

  const handleDeleteUser = async () => {
    if (deleteUserId === '---') {
      toast.error('❌ Επιλέξτε χειριστή');
      return;
    }
    
    const user = users.find(u => u.id === parseInt(deleteUserId));
    if (!user) return;
    
    if (!window.confirm(`🚨 ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ χειριστή '${user.username}';`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/users/${deleteUserId}?admin_password=${encodeURIComponent(userManagementPassword)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.warning(`🗑️ Ο χειριστής '${user.username}' διαγράφηκε!`);
      setDeleteUserId('---');
      
      // Reload users
      handleUserManagementUnlock();
    } catch (error) {
      toast.error('❌ ' + (error.response?.data?.detail || 'Σφάλμα διαγραφής'));
    }
  };

  // ============ FACTORY RESET ============
  
  const handleFactoryReset = async () => {
    // Final confirmation
    const confirmText = `🚨 ΤΕΛΕΥΤΑΙΑ ΠΡΟΕΙΔΟΠΟΙΗΣΗ 🚨

ΟΛΑ τα δεδομένα θα διαγραφούν ΜΟΝΙΜΑ:
• Πελάτες
• Μηχανήματα
• Μετρήσεις
• Service Logs
• Αναλωσίμα
• Κατάλογος

Η εταιρεία θα μετονομαστεί σε: "${newCompanyName}"

ΔΕΝ ΥΠΑΡΧΕΙ UNDO!

Θέλετε να συνεχίσετε;`;

    if (!window.confirm(confirmText)) {
      toast.info('✋ Ακυρώθηκε');
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/settings/factory-reset`,
        {
          password: factoryResetPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { new_company_name: newCompanyName }
        }
      );

      toast.success(`✅ ${response.data.message}`);
      toast.success(`🏢 Νέο όνομα εταιρείας: ${response.data.company_name}`);
      
      // Reset form
      setShowFactoryResetForm(false);
      setFactoryResetPassword('');
      setNewCompanyName('');
      
      // Reload DB info
      loadDbInfo();
      
      // Show success message
      setTimeout(() => {
        toast.info('💡 Μπορείτε τώρα να ξεκινήσετε να προσθέτετε νέους πελάτες και δεδομένα!');
      }, 2000);

    } catch (error) {
      console.error('Factory reset error:', error);
      
      if (error.response?.status === 403) {
        toast.error('❌ Λάθος κωδικός admin!');
      } else {
        toast.error('❌ ' + (error.response?.data?.detail || 'Σφάλμα factory reset'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900">💾 Backup & Restore ΒΑΣΗΣ ΔΕΔΟΜΕΝΩΝ</h1>
        <p className="text-gray-600 mt-1">Διαχείριση αντιγράφων ασφαλείας και δεδομένων</p>
      </div>

      {/* ============ SECTION 1: DB BACKUP/RESTORE ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Backup */}
        <Card className="border-l-4 border-l-green-500">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500 rounded-lg">
                <Download className="h-6 w-6 text-white" />
              </div>
              <CardTitle>📤 ΔΗΜΙΟΥΡΓΙΑ Backup</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600 text-sm">
              ΚΑΤΕΒΑΣΤΕ ΑΝΤΙΓΡΑΦΟ ΑΣΦΑΛΕΙΑΣ ΤΗΣ ΒΑΣΗΣ ΔΕΔΟΜΕΝΩΝ.
            </p>
            
            <Button 
              onClick={handleBackup}
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={loading}
            >
              <Download className="h-4 w-4 mr-2" />
              {loading ? 'Δημιουργία...' : '🔄 ΔΗΜΙΟΥΡΓΙΑ Backup'}
            </Button>

            {dbInfo && (
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm space-y-1">
                <p className="font-semibold">📊 ΜΕΓΕΘΟΣ ΒΑΣΗΣ: {dbInfo.size_kb} KB</p>
                <p className="text-gray-700">📅 ΤΕΛΕΥΤΑΙΑ ΤΡΟΠΟΠΟΙΗΣΗ: {dbInfo.last_modified}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Restore - Admin Only */}
        {isAdmin && (
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-500 rounded-lg">
                <Upload className="h-6 w-6 text-white" />
              </div>
              <CardTitle>📥 ΕΠΑΝΑΦΟΡΑ Backup</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-orange-800">
                  ⚠️ Η ΕΠΑΝΑΦΟΡΑ ΘΑ ΑΝΤΙΚΑΤΑΣΤΗΣΕΙ ΤΗΝ ΤΡΕΧΟΥΣΑ ΒΑΣΗ ΔΕΔΟΜΕΝΩΝ!
                </p>
              </div>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <input
                type="file"
                accept=".json"
                onChange={(e) => setBackupFile(e.target.files[0])}
                className="hidden"
                id="backup-upload"
              />
              <label htmlFor="backup-upload" className="cursor-pointer">
                <span className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50 font-medium text-gray-700 transition-colors">
                  <Upload className="h-4 w-4 mr-2" />
                  ΕΠΙΛΕΞΤΕ ΑΡΧΕΙΟ backup (.json)
                </span>
              </label>
              {backupFile && (
                <p className="text-sm text-green-600 mt-2 font-medium">
                  ✓ 📄 {backupFile.name} ({(backupFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">🔑 ΚΩΔΙΚΟΣ Admin</label>
              <Input
                type="password"
                value={restorePassword}
                onChange={(e) => setRestorePassword(e.target.value)}
                placeholder="Εισάγετε κωδικό admin"
              />
            </div>

            <Button 
              onClick={handleRestore}
              disabled={!backupFile || loading}
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              {loading ? 'Επαναφορά...' : '🔄 ΕΠΑΝΑΦΟΡΑ ΒΑΣΗΣ'}
            </Button>
          </CardContent>
        </Card>
        )}
      </div>

      {/* ============ SECTION 2: AUTO BACKUP ============ */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500 rounded-lg">
              <Settings className="h-6 w-6 text-white" />
            </div>
            <CardTitle>ΑΥΤΟΜΑΤΟ BACKUP</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            ΕΝΕΡΓΟΠΟΙΗΣΤΕ ΤΟ ΑΥΤΟΜΑΤΟ BACKUP ΣΤΟ SERVER. ΤΑ ΑΝΤΙΓΡΑΦΑ ΑΠΟΘΗΚΕΥΟΝΤΑΙ ΑΥΤΟΜΑΤΑ ΣΤΗΝ ΒΑΣΗ.
          </p>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="auto-backup-toggle"
              checked={autoBackupEnabled}
              onChange={(e) => handleAutoBackupToggle(e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              data-testid="auto-backup-toggle"
            />
            <label htmlFor="auto-backup-toggle" className="font-medium">
              ΕΝΕΡΓΟΠΟΙΗΣΗ ΑΥΤΟΜΑΤΟΥ BACKUP
            </label>
          </div>

          {autoBackupEnabled && (
            <div className="space-y-3 pl-8 border-l-2 border-blue-200">
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Clock className="inline h-4 w-4 mr-1" />
                  ΣΥΧΝΟΤΗΤΑ (ΩΡΑ ΑΝΑ BACKUP)
                </label>
                <select
                  value={autoBackupInterval}
                  onChange={(e) => handleAutoBackupIntervalChange(e.target.value)}
                  className="w-full p-2 border rounded"
                  data-testid="auto-backup-interval"
                >
                  <option value="1">Κάθε 1 ώρα</option>
                  <option value="6">Κάθε 6 ώρες</option>
                  <option value="12">Κάθε 12 ώρες</option>
                  <option value="24">Κάθε 24 ώρες (ημερήσιο)</option>
                  <option value="48">Κάθε 48 ώρες</option>
                  <option value="168">Κάθε εβδομάδα</option>
                </select>
              </div>

              <Button 
                onClick={handleTriggerAutoBackup}
                className="w-full"
                variant="outline"
                disabled={loadingAutoBackup}
                data-testid="trigger-auto-backup-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingAutoBackup ? 'animate-spin' : ''}`} />
                {loadingAutoBackup ? 'ΔΗΜΙΟΥΡΓΙΑ...' : 'ΔΗΜΙΟΥΡΓΙΑ BACKUP ΤΩΡΑ'}
              </Button>

              {autoBackupLastRun && (
                <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm">
                  <p>
                    <Clock className="inline h-4 w-4 mr-1" />
                    ΤΕΛΕΥΤΑΙΟ BACKUP: {new Date(autoBackupLastRun).toLocaleString('el-GR')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Auto Backup History */}
          {autoBackupList.length > 0 && (
            <div className="mt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <History className="h-4 w-4" />
                ΙΣΤΟΡΙΚΟ AUTO BACKUPS
              </h4>
              <div className="border rounded max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="p-2 text-left">ΗΜΕΡΟΜΗΝΙΑ</th>
                      <th className="p-2 text-left">ΕΓΓΡΑΦΕΣ</th>
                      <th className="p-2 text-left">ΑΠΟ</th>
                      <th className="p-2 text-right">ΕΝΕΡΓΕΙΕΣ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoBackupList.map((backup, idx) => (
                      <tr key={idx} className="border-t hover:bg-gray-50">
                        <td className="p-2 text-xs">{new Date(backup.timestamp).toLocaleString('el-GR')}</td>
                        <td className="p-2">{backup.total_records}</td>
                        <td className="p-2 text-xs">{backup.triggered_by}</td>
                        <td className="p-2 text-right">
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => handleDownloadAutoBackup(backup.timestamp)}
                              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                              data-testid={`download-auto-backup-${idx}`}
                            >
                              ΛΗΨΗ
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => handleRestoreAutoBackup(backup.timestamp)}
                                className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors"
                                data-testid={`restore-auto-backup-${idx}`}
                              >
                                ΕΠΑΝΑΦΟΡΑ
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteAutoBackup(backup.timestamp)}
                              className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                              data-testid={`delete-auto-backup-${idx}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ SECTION 3: EXPORT / IMPORT ============ */}
      <Card className="border-l-4 border-l-purple-500">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-500 rounded-lg">
              <FileSpreadsheet className="h-6 w-6 text-white" />
            </div>
            <CardTitle>📋 EXPORT / IMPORT ΠΙΝΑΚΩΝ</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            ΕΞΑΓΩΓΗ ΚΑΙ ΕΙΣΑΓΩΓΗ ΔΕΔΟΜΕΝΩΝ ΑΝΑ ΠΙΝΑΚΑ ΣΕ EXCEL/CSV/XML ΓΙΑ ΜΑΖΙΚΕΣ ΑΛΛΑΓΕΣ.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* EXPORT */}
            <div className="space-y-4 border-r pr-6">
              <h3 className="font-semibold text-lg">📤 EXPORT ΠΙΝΑΚΑ</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">ΕΠΙΛΕΞΤΕ ΠΙΝΑΚΑ ΓΙΑ EXPORT</label>
                <select
                  value={exportTable}
                  onChange={(e) => handleExportTableSelect(e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="---">---</option>
                  {TABLES.map(table => (
                    <option key={table} value={table}>{table}</option>
                  ))}
                </select>
              </div>

              {exportTable !== '---' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">ΜΟΡΦΗ ΑΡΧΕΙΟΥ</label>
                    <select
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="xlsx">Excel (.xlsx)</option>
                      <option value="csv">CSV (.csv)</option>
                      <option value="xml">XML (.xml)</option>
                    </select>
                  </div>

                  <Button 
                    onClick={handleExport}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    📥 ΛΗΨΗ {exportTable}
                  </Button>
                </>
              )}
            </div>

            {/* IMPORT - Admin Only */}
            {isAdmin && (
            <div className="space-y-4 pl-6">
              <h3 className="font-semibold text-lg">📥 IMPORT ΠΙΝΑΚΑ</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">ΕΠΙΛΕΞΤΕ ΠΙΝΑΚΑ ΓΙΑ IMPORT</label>
                <select
                  value={importTable}
                  onChange={(e) => setImportTable(e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="---">---</option>
                  {TABLES.map(table => (
                    <option key={table} value={table}>{table}</option>
                  ))}
                </select>
              </div>

              {importTable !== '---' && (
                <>
                  <div className="bg-orange-50 border border-orange-200 p-2 rounded text-sm">
                    <AlertTriangle className="h-4 w-4 inline mr-1 text-orange-600" />
                    ⚠️ ΤΟ IMPORT ΘΑ ΑΝΤΙΚΑΤΑΣΤΗΣΕΙ ΟΛΑ ΤΑ ΔΕΔΟΜΕΝΑ ΤΟΥ ΠΙΝΑΚΑ!
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      ΑΝΕΒΑΣΤΕ ΑΡΧΕΙΟ (.xlsx, .csv, .xml)
                    </label>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv,.xml"
                      onChange={(e) => handleImportFileSelect(e, importTable)}
                      className="w-full p-2 border rounded text-sm"
                    />
                    {importFile && (
                      <p className="text-sm text-green-600 mt-1">
                        ✓ {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="import-backup-confirm"
                      checked={importBackupConfirm}
                      onChange={(e) => setImportBackupConfirm(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="import-backup-confirm" className="text-sm font-medium">
                      ✅ ΕΧΕΤΕ ΚΑΝΕΙ BACKUP ΠΡΙΝ ΤΟ IMPORT;
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">🔑 ΚΩΔΙΚΟΣ ADMIN</label>
                    <Input
                      type="password"
                      value={importPassword}
                      onChange={(e) => setImportPassword(e.target.value)}
                      placeholder="Εισάγετε κωδικό admin"
                    />
                  </div>

                  <Button 
                    onClick={handleImport}
                    disabled={!importFile || loading}
                    className="w-full bg-orange-600 hover:bg-orange-700"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    🔄 ΕΚΤΕΛΕΣΗ IMPORT
                  </Button>
                </>
              )}
            </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ============ SECTION 4: USER MANAGEMENT (Admin Only) ============ */}
      {isAdmin && (
      <Card className="border-l-4 border-l-indigo-500">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-500 rounded-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <CardTitle>👥 ΔΙΑΧΕΙΡΙΣΗ ΧΕΙΡΙΣΤΩΝ</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            ΠΡΟΣΘΗΚΗ, ΑΝΑΣΤΟΛΗ ΚΑΙ ΔΙΑΓΡΑΦΗ ΧΕΙΡΙΣΤΩΝ.
          </p>

          {!showUserManagement ? (
            <div className="space-y-3">
              <Button 
                onClick={handleUserManagementUnlock}
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                <Lock className="h-4 w-4 mr-2" />
                ΑΝΟΙΓΜΑ ΔΙΑΧΕΙΡΙΣΗΣ
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Users Table */}
              <div>
                <h4 className="font-semibold mb-3 text-lg">ΛΙΣΤΑ ΧΕΙΡΙΣΤΩΝ</h4>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left">ID</th>
                        <th className="p-2 text-left">ΧΕΙΡΙΣΤΗΣ</th>
                        <th className="p-2 text-left">ΚΑΤΑΣΤΑΣΗ</th>
                        <th className="p-2 text-left">ΗΜΕΡΟΜΗΝΙΑ ΔΗΜΙΟΥΡΓΙΑΣ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id} className="border-t hover:bg-gray-50">
                          <td className="p-2">{user.id}</td>
                          <td className="p-2 font-medium">{user.username}</td>
                          <td className="p-2">
                            {user.status === 'active' ? (
                              <span className="text-green-600">✅ active</span>
                            ) : (
                              <span className="text-orange-600">⏸️ paused</span>
                            )}
                          </td>
                          <td className="p-2 text-gray-600">
                            {new Date(user.created_date).toLocaleDateString('el-GR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Add User Form */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">➕ ΠΡΟΣΘΗΚΗ ΝΕΟΥ ΧΕΙΡΙΣΤΗ</h4>
                <form onSubmit={handleCreateUser} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">USERNAME</label>
                      <Input
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        placeholder="Username"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">PASSWORD</label>
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Password"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    ✅ ΔΗΜΙΟΥΡΓΙΑ ΧΕΙΡΙΣΤΗ
                  </Button>
                </form>
              </div>

              {/* Toggle User Status */}
              <div>
                <h4 className="font-semibold mb-3">⏸️ ΑΝΑΣΤΟΛΗ / ΕΝΕΡΓΟΠΟΙΗΣΗ ΧΕΙΡΙΣΤΗ</h4>
                <div className="flex gap-3">
                  <select
                    value={toggleUserId}
                    onChange={(e) => setToggleUserId(e.target.value)}
                    className="flex-1 p-2 border rounded"
                  >
                    <option value="---">ΕΠΙΛΕΞΤΕ ΧΕΙΡΙΣΤΗ</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username} ({user.status})
                      </option>
                    ))}
                  </select>
                  <Button 
                    onClick={handleToggleUser}
                    disabled={toggleUserId === '---'}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    🔄 TOGGLE
                  </Button>
                </div>
              </div>

              {/* Delete User */}
              <div>
                <h4 className="font-semibold mb-3">🗑️ ΔΙΑΓΡΑΦΗ ΧΕΙΡΙΣΤΗ</h4>
                <div className="flex gap-3">
                  <select
                    value={deleteUserId}
                    onChange={(e) => setDeleteUserId(e.target.value)}
                    className="flex-1 p-2 border rounded"
                  >
                    <option value="---">ΧΕΙΡΙΣΤΗΣ ΠΡΟΣ ΔΙΑΓΡΑΦΗ</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username}
                      </option>
                    ))}
                  </select>
                  <Button 
                    onClick={handleDeleteUser}
                    disabled={deleteUserId === '---'}
                    variant="destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    🚨 ΔΙΑΓΡΑΦΗ
                  </Button>
                </div>
              </div>

              {/* Activity Logs */}
              <div>
                <h4 className="font-semibold mb-3">📜 ΙΣΤΟΡΙΚΟ ΕΝΕΡΓΕΙΩΝ ΧΕΙΡΙΣΤΩΝ</h4>
                <div className="border rounded max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">ΧΕΙΡΙΣΤΗΣ</th>
                        <th className="p-2 text-left">ΕΝΕΡΓΕΙΑ</th>
                        <th className="p-2 text-left">ΗΜΕΡΟΜΗΝΙΑ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLogs.length > 0 ? (
                        activityLogs.map((log, idx) => (
                          <tr key={idx} className="border-t hover:bg-gray-50">
                            <td className="p-2">{log.username}</td>
                            <td className="p-2">{log.action}</td>
                            <td className="p-2 text-gray-600 text-xs">
                              {new Date(log.timestamp).toLocaleString('el-GR')}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="p-4 text-center text-gray-500">
                            ΔΕΝ ΥΠΑΡΧΟΥΝ ΚΑΤΑΓΕΓΡΑΜΜΕΝΕΣ ΕΝΕΡΓΕΙΕΣ.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* ============ FACTORY RESET - ΝΕΑ ΕΓΚΑΤΑΣΤΑΣΗ (Admin Only) ============ */}
      {isAdmin && (
      <Card className="border-red-200 bg-red-50">
        <CardHeader className="bg-red-100">
          <CardTitle className="flex items-center gap-2 text-red-800">
            <AlertTriangle className="h-6 w-6" />
            🚨 ΝΕΑ ΕΓΚΑΤΑΣΤΑΣΗ (FACTORY RESET)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="bg-white p-4 rounded border-2 border-red-300">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-red-800 text-lg mb-2">
                  ⚠️ ΠΡΟΣΟΧΗ: ΜΗ ΕΠΙΣΤΡΕΨΙΜΗ ΔΙΑΔΙΚΑΣΙΑ
                </h3>
                <p className="text-gray-700 mb-2">
                  Η λειτουργία "ΝΕΑ ΕΓΚΑΤΑΣΤΑΣΗ" θα:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li><strong className="text-red-600">Διαγράψει ΟΛΟΥΣ τους Πελάτες</strong></li>
                  <li><strong className="text-red-600">Διαγράψει ΟΛΑ τα Μηχανήματα</strong></li>
                  <li><strong className="text-red-600">Διαγράψει ΟΛΕΣ τις Μετρήσεις</strong></li>
                  <li><strong className="text-red-600">Διαγράψει ΟΛΑ τα Service Logs</strong></li>
                  <li><strong className="text-red-600">Διαγράψει ΟΛΑ τα Αναλωσίμα</strong></li>
                  <li><strong className="text-red-600">Διαγράψει τον Κατάλογο Μηχανημάτων</strong></li>
                  <li className="text-green-700"><strong>Θα κρατήσει</strong> τους χειριστές του συστήματος</li>
                </ul>
                <p className="text-red-700 font-bold mt-3">
                  ⚠️ ΔΕΝ ΥΠΑΡΧΕΙ UNDO! Κάντε backup πριν συνεχίσετε!
                </p>
              </div>
            </div>

            {!showFactoryResetForm ? (
              <Button
                onClick={() => setShowFactoryResetForm(true)}
                variant="destructive"
                className="w-full bg-red-600 hover:bg-red-700"
              >
                <Settings className="h-5 w-5 mr-2" />
                ΝΕΑ ΕΓΚΑΤΑΣΤΑΣΗ
              </Button>
            ) : (
              <div className="space-y-4 bg-yellow-50 p-4 rounded border border-yellow-300">
                <div>
                  <label className="block font-semibold mb-2 text-red-800">
                    <Lock className="inline h-4 w-4 mr-1" />
                    Κωδικός Επιβεβαίωσης:
                  </label>
                  <Input
                    type="password"
                    value={factoryResetPassword}
                    onChange={(e) => setFactoryResetPassword(e.target.value)}
                    placeholder="Εισάγετε κωδικό"
                    className="border-red-300"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2 text-gray-800">
                    Νέο Όνομα Εταιρείας:
                  </label>
                  <Input
                    type="text"
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    placeholder='π.χ. "ΔΗΜΟΠΟΥΛΟΣ Α.Ε." ή "SMITH CO."'
                    className="border-blue-300"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Αυτό το όνομα θα αντικαταστήσει το "ΚΥΡΙΑΚΙΔΗΣ Ε.Ε." παντού στην εφαρμογή και τα reports.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setShowFactoryResetForm(false);
                      setFactoryResetPassword('');
                      setNewCompanyName('');
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Άκυρο
                  </Button>
                  <Button
                    onClick={handleFactoryReset}
                    disabled={!factoryResetPassword || !newCompanyName}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    🚨 ΕΠΙΒΕΒΑΙΩΣΗ ΔΙΑΓΡΑΦΗΣ
                  </Button>
                </div>

                <p className="text-xs text-center text-red-600 font-semibold">
                  Πατώντας "ΕΠΙΒΕΒΑΙΩΣΗ ΔΙΑΓΡΑΦΗΣ" θα χάσετε ΟΛΕΣ τις πληροφορίες μόνιμα!
                </p>
              </div>
            )}
          </div>

          {/* Use Cases */}
          <div className="bg-blue-50 p-4 rounded border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">
              💡 Πότε να χρησιμοποιήσετε αυτή τη λειτουργία:
            </h4>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>✅ Θέλετε να δώσετε το πρόγραμμα σε άλλη εταιρεία</li>
              <li>✅ Θέλετε να ξεκινήσετε από την αρχή με καθαρά δεδομένα</li>
              <li>✅ Θέλετε να αλλάξετε το όνομα της εταιρείας στο σύστημα</li>
              <li>❌ ΜΗΝ χρησιμοποιήσετε για testing - Κάντε backup!</li>
            </ul>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default BackupPage;
